import classNames from 'classnames';
import { FC, useCallback, useContext, useTransition } from 'react';

import { CabinetFilters, CabinetTemplate } from '@/widgets/Templates';
import {
  FilterField,
  FilterStatus,
  useCabinetFilter,
} from '@/widgets/Templates/CabinetTemplate/ui/CabinetFilters/hook/useCabinetFilter';

import { IntegrationSystem } from '@/shared/types';
import { Button, ContentField, FormSelect, FormTextInput, Icon, Typography } from '@/shared/ui';

import { Loader } from '@/shared/ui';

import { saveFileFromBlob } from '@/helpers/saveFileFromBlob';

import AuthContext from '@/contexts/Auth/AuthContext';
import ModalContext from '@/contexts/Modal/ModalContext';
import { ModalName } from '@/contexts/Modal/modalTypes';

import { queryKeys } from '@/api/queryKeys';
import { ERZPermit, Weapon_v2 } from '@/api/types';

import api from '@/api';

import { PermitList } from './children/PermitLists/PermitList';

import './ErzExtract.scss';

type WeaponPart_Firearm = {
  type: string;
  caliber: Nullable<string>;
  brandName: Nullable<string>;
  serialNum: Nullable<string>;
  vendor?: Nullable<string>;
};

export interface ErzPermit {
  id: string;
  permissionName: string | null;
  permitNum: string;
  weapon: {
    typeName?: string;
    model?: string;
    serialNumber?: Weapon_v2['serialNumber'];
    caliber?: string;
    parts?: WeaponPart_Firearm[];
  };
  permitInfo: {
    issuedAt: string;
    expiredAt?: string;
    canExtract: boolean;
    permitType?: Nullable<string>;
  };
}

const mergePermitsWithWeapons = (
  permits: ERZPermit[],
  weapons: Weapon_v2[],
  permitTypes: Array<{ label: string; name: string }>,
): ErzPermit[] => {
  return permits.map((permit) => {
    const permissionName = permitTypes.find((pt) => pt.label === permit.permitType)?.name || null;

    const relatedWeapon =
      permit.objects
        .map((obj) => weapons.find((weapon) => weapon.id === obj.id && weapon.weaponType === obj.type))
        .find(Boolean) || null;

    return {
      id: permit.id,
      permissionName,
      permitNum: permit?.num,
      weapon: {
        typeName: relatedWeapon?.typeName,
        model: relatedWeapon?.weaponType === 'firearm_part' ? undefined : relatedWeapon?.model,
        serialNumber: relatedWeapon?.serialNumber,
        caliber:
          relatedWeapon?.weaponType === 'firearm_part'
            ? undefined
            : relatedWeapon?.extra && 'weaponInfo' in relatedWeapon.extra
              ? (relatedWeapon.extra.weaponInfo.caliber ?? undefined)
              : undefined,
        parts: relatedWeapon?.weaponType === 'firearm' ? relatedWeapon.parts : undefined,
      },
      permitInfo: {
        issuedAt: permit.issuedAt,
        expiredAt: permit.expiredAt,
        canExtract: permit.canExtract,
        permitType: permit.permitType,
      },
    };
  });
};
const filterFields: FilterField[] = [
  {
    filterKey: 'permissionName',
    type: 'select',
  },
  {
    filterKey: 'permitNum',
    type: 'text',
  },
];

export const ErzExtractPage: FC = () => {
  const { systems } = useContext(AuthContext);
  const { renderSpecificModal } = useContext(ModalContext);
  const [areExtractsLoading, startExtractLoading] = useTransition();
  const loadDataFn = useCallback(() => {
    return Promise.all([api.permits.getAll(), api.dictionary.getPermitTypes(), api.getWeaponsData_v2()]).then(
      ([{ data: userPermits = [] }, { data: permitTypes = [] }, { data: weaponData }]) => {
        const weapons = weaponData.weapons || [];
        const permitTypesArray: Array<{ label: string; name: string }> = Array.isArray(permitTypes)
          ? permitTypes
          : (Object.values(permitTypes) as Array<{ label: string; name: string }>);
        return {
          data: mergePermitsWithWeapons(userPermits, weapons, permitTypesArray),
        };
      },
    );
  }, []);

  const { filteredData, onFilter, onReset, selectOptionsMap, status } = useCabinetFilter<ErzPermit>({
    queryKey: queryKeys.permits.list(),
    loadDataFn,
    sortByField: 'issuedAt',
    filterFields,
  });

  const getContent = () => {
    switch (status) {
      case FilterStatus.LOADING:
        return <Loader centered />;
      case FilterStatus.LOAD_ERROR:
        return (
          <ContentField
            icon="hi"
            content={
              <Typography color="graphite" textCenter>
                Наразі у вас немає жодного витягу по дозволам.
                <br />
                Коли ви замовите перший витяг, він зʼявиться тут.
              </Typography>
            }
          />
        );

      case FilterStatus.NO_FILTER_RESULTS:
        return (
          <>
            <CabinetFilters
              inputs={(methods) => (
                <>
                  <FormSelect
                    label="Витяг ЄРЗ"
                    name="permissionName"
                    control={methods.control}
                    options={selectOptionsMap['permissionName']}
                  />
                  <FormTextInput label="Номер дозволу" name="permitNum" control={methods.control} />
                </>
              )}
              onClear={onReset}
              onSearch={onFilter}
            />
            <ContentField
              icon="no_result"
              content={
                <Typography color="graphite" textCenter>
                  За вашим запитом нічого не знайдено.
                </Typography>
              }
            />
          </>
        );

      case FilterStatus.SUCCESS:
      default:
        return (
          <>
            <CabinetFilters
              inputs={(methods) => (
                <>
                  <FormSelect
                    label="Витяг ЄРЗ"
                    name="permissionName"
                    control={methods.control}
                    options={selectOptionsMap['permissionName']}
                  />
                  <FormTextInput label="Номер дозволу" name="permitNum" control={methods.control} />
                </>
              )}
              onClear={onReset}
              onSearch={onFilter}
            />
            <PermitList erzExtracts={filteredData} />
          </>
        );
    }
  };

  return (
    <CabinetTemplate>
      <section className="erz-extract container-with-aside__section">
        <div className="erz-extract__title">
          <Typography variant="H2">Дозволи</Typography>
          {systems.includes(IntegrationSystem.ERZ) && (
            <Button
              className={classNames('erz-extract__download-extract', {
                'erz-extract__download-extract--loading': areExtractsLoading,
              })}
              disabled={areExtractsLoading}
              onClick={(): void => {
                startExtractLoading(() => {
                  api.permits
                    .downloadExtract()
                    .then(({ file, fileName }) => saveFileFromBlob(fileName ?? '', file))
                    .catch(() => {
                      renderSpecificModal({
                        modalName: ModalName.ServerErrorAlert,
                      });
                    });
                });
              }}
            >
              {areExtractsLoading ? <Loader color="white" /> : <Icon name="download" />}
              Витяг про наявні дозволи
            </Button>
          )}
        </div>
        {getContent()}
      </section>
    </CabinetTemplate>
  );
};
